import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useSSEConnected } from '../../hooks/useSSE'
import { fetchActivityFeed } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import { timeAgo } from '../../hooks/useTimeAgo'
import type { ActivityEvent } from '../../lib/types'

interface LogEntry {
  id: number
  persona: string
  action_type: string
  summary: string
  context: string | null
  timestamp: string
}

interface UnifiedEntry {
  id: string
  actor: string
  type: string
  summary: string
  detail?: string
  timestamp: string
  source: 'event' | 'log'
  link?: string
  context?: Record<string, unknown>
}

const EVENT_ICONS: Record<string, string> = {
  message: '💬', task: '✓', project: '📁', file: '📎',
  conversation: '🔗', approval: '✋', persona: '👤', system: '⚙',
  thread: '💬', file_uploaded: '📎', conversation_created: '🔗',
  project_created: '📁', task_completed: '✓', ceremony: '🔥',
  thread_post: '💬', task_created: '📋', task_status_change: '✓', queue_message: '📬',
}

const TYPE_LABELS: Record<string, string> = {
  message: 'Message', task: 'Task', project: 'Project', file: 'File',
  conversation: 'Conversation', approval: 'Approval', persona: 'Persona', system: 'System',
  thread: 'Thread', file_uploaded: 'File', conversation_created: 'New Conversation',
  project_created: 'New Project', task_completed: 'Task Complete', ceremony: 'Ceremony',
  thread_post: 'Thread', task_created: 'New Task', task_status_change: 'Task Update', queue_message: 'Queue',
}

const TYPE_COLORS: Record<string, string> = {
  message: '#58a6ff', task: '#3fb950', project: '#d29922', file: '#8b949e',
  conversation: '#bc8cff', approval: '#f85149', thread: '#58a6ff',
  conversation_created: '#bc8cff', project_created: '#d29922', task_completed: '#3fb950',
  ceremony: '#D4A054', file_uploaded: '#8b949e',
  thread_post: '#58a6ff', task_created: '#d29922', task_status_change: '#3fb950', queue_message: '#bc8cff',
}

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'task', label: '✓ Tasks' },
  { key: 'message', label: '💬 Messages' },
  { key: 'queue', label: '📬 Queue' },
  { key: 'project', label: '📁 Projects' },
  { key: 'file', label: '📎 Files' },
] as const

const COMMUNITY_VISIBLE_TYPES = new Set([
  'conversation_created', 'project_created', 'task_completed', 'task',
  'file', 'file_uploaded', 'approval', 'ceremony', 'project', 'conversation',
])

function smartDayLabel(dateStr: string): string {
  const ts = dateStr.includes('Z') || dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z'
  const date = new Date(ts)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  if (eventDay.getTime() === today.getTime()) return 'Today'
  if (eventDay.getTime() === yesterday.getTime()) return 'Yesterday'
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

function normalizeLogEntry(e: LogEntry): UnifiedEntry {
  let ctx: Record<string, unknown> = {}
  if (e.context) try { ctx = JSON.parse(e.context) } catch { /* */ }

  let link: string | undefined
  if (ctx.project_id) link = `/project/${ctx.project_id}`
  else if (ctx.conversation_id) link = `/conversation/${ctx.conversation_id}`

  return {
    id: `log-${e.id}`,
    actor: (e.persona || '').toLowerCase(),
    type: e.action_type,
    summary: e.summary,
    detail: ctx.task_id ? `Task: ${ctx.task_id}` : undefined,
    timestamp: e.timestamp.includes('T') ? e.timestamp : e.timestamp.replace(' ', 'T') + 'Z',
    source: 'log',
    link,
    context: ctx,
  }
}

function normalizeEvent(e: ActivityEvent): UnifiedEntry {
  let link: string | undefined
  if (e.conversation_id) link = `/conversation/${e.conversation_id}`
  else if (e.project_id) link = `/project/${e.project_id}`
  const ext = e as ActivityEvent & Record<string, unknown>
  const preview = (ext.preview || ext.content || ext.body || '') as string

  return {
    id: `evt-${e.id}`,
    actor: e.actor,
    type: e.event_type,
    summary: e.title || e.description || preview.substring(0, 120) || '',
    timestamp: e.created_at,
    source: 'event',
    link,
  }
}

function matchesFilter(entry: UnifiedEntry, filter: string): boolean {
  if (filter === 'all') return true
  const typeMap: Record<string, string[]> = {
    task: ['task', 'task_status_change', 'task_created', 'task_completed'],
    message: ['message', 'thread', 'thread_post'],
    queue: ['queue_message'],
    project: ['project', 'project_created'],
    file: ['file', 'file_uploaded'],
  }
  return (typeMap[filter] || [filter]).includes(entry.type)
}

function EntryCard({ entry }: { entry: UnifiedEntry }) {
  const color = TYPE_COLORS[entry.type] || '#8b949e'
  const icon = EVENT_ICONS[entry.type] || '•'
  const label = TYPE_LABELS[entry.type] || entry.type.replace(/_/g, ' ')

  const content = (
    <div className="flex gap-3 px-3 py-2.5 rounded-md hover:bg-[var(--bg-elevated)] transition-colors cursor-pointer">
      <PersonaAvatar name={entry.actor} size={24} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs">{icon}</span>
          <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{entry.actor}</span>
          <span className="text-xs px-1.5 py-0.5 rounded capitalize" style={{
            background: `${color}20`, color, fontSize: 10,
          }}>
            {label}
          </span>
        </div>
        {entry.summary && (
          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-secondary)' }}>
            {entry.summary.substring(0, 140)}
          </div>
        )}
        {entry.detail && (
          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {entry.detail}
          </div>
        )}
      </div>
      <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
        {timeAgo(entry.timestamp)}
      </span>
    </div>
  )

  if (entry.link) return <Link to={entry.link} className="no-underline block">{content}</Link>
  return content
}

export default function ActivityPage() {
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const isCommunity = user?.userType === 'community'
  const [typeFilter, setTypeFilter] = useState('all')
  const [personaFilter, setPersonaFilter] = useState('')
  const [limit, setLimit] = useState(100)

  const { data: eventData, isLoading: eventsLoading } = useQuery({
    queryKey: ['activity-events', limit],
    queryFn: () => fetchActivityFeed(limit),
    refetchInterval: sseOk ? false : 30_000,
  })

  const { data: logData, isLoading: logLoading } = useQuery({
    queryKey: ['activity-log', limit],
    queryFn: async () => {
      const res = await fetch(`/api/ops/activity?limit=${limit}`)
      if (!res.ok) return []
      const data = await res.json()
      return Array.isArray(data) ? data : (data.entries || data.data || [])
    },
    refetchInterval: 10_000,
    enabled: !isCommunity,
  })

  const unified = useMemo(() => {
    const events = (eventData?.events || []).map(normalizeEvent)
    const logs = (logData || []).map(normalizeLogEntry)

    // Dedupe: if an event and log entry have very similar timestamps and actors, keep the log (richer)
    const seen = new Set<string>()
    const deduped: UnifiedEntry[] = []
    for (const e of [...logs, ...events]) {
      const key = `${e.actor}-${e.type}-${e.timestamp.substring(0, 16)}`
      if (seen.has(key)) continue
      seen.add(key)
      deduped.push(e)
    }

    let filtered = deduped
    if (isCommunity && typeFilter === 'all') {
      filtered = filtered.filter(e => e.source === 'log' || COMMUNITY_VISIBLE_TYPES.has(e.type))
    }
    filtered = filtered.filter(e => matchesFilter(e, typeFilter))
    if (personaFilter) filtered = filtered.filter(e => e.actor.toLowerCase() === personaFilter.toLowerCase())

    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    return filtered
  }, [eventData, logData, typeFilter, personaFilter, isCommunity])

  const personas = useMemo(() => {
    const set = new Set<string>()
    for (const e of unified) set.add(e.actor)
    return [...set].sort()
  }, [unified])

  const grouped: Record<string, UnifiedEntry[]> = {}
  for (const e of unified) {
    const day = smartDayLabel(e.timestamp)
    if (!grouped[day]) grouped[day] = []
    grouped[day].push(e)
  }

  const isLoading = eventsLoading || logLoading

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-4 mb-4">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Activity</h1>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{unified.length} events</span>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1">
          {FILTER_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              className="text-xs px-2.5 py-1 rounded-md transition-colors"
              style={{
                background: typeFilter === t.key ? 'var(--bg-elevated)' : 'transparent',
                color: typeFilter === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
                border: typeFilter === t.key ? '1px solid var(--border)' : '1px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {!isCommunity && (
          <select
            value={personaFilter}
            onChange={e => setPersonaFilter(e.target.value)}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '4px 8px', color: 'var(--text-primary)',
              fontSize: 11, outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">All personas</option>
            {personas.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <>
          {Object.entries(grouped).map(([day, dayEntries]) => (
            <section key={day} className="mb-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-2 pb-1" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                {day}
              </h2>
              <div className="flex flex-col">
                {dayEntries.map(e => <EntryCard key={e.id} entry={e} />)}
              </div>
            </section>
          ))}

          {unified.length >= limit && (
            <div className="text-center py-4">
              <button
                onClick={() => setLimit(l => l + 100)}
                className="text-xs px-4 py-2 rounded-md"
                style={{ background: 'var(--bg-elevated)', color: '#58a6ff', border: '1px solid var(--border)', cursor: 'pointer' }}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}

      {!isLoading && unified.length === 0 && (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No activity yet</p>
      )}
    </div>
  )
}
